import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListItem,
  TagLabel,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import { useCombobox, useMultipleSelection } from "downshift";
import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { SelectedItemTag } from "./SelectedItemTag";
import { BaseGenericType, MultiSelectMenuBaseProps } from "./types";

export interface TagMultiSelectMenuProps<T>
  extends MultiSelectMenuBaseProps<T> {
  selectedItemsContainerProps?: FlexProps;
  renderTagContent?: (item: T) => React.ReactElement;
  validateSelectedItems?: boolean;
  invalidSelectionErrorMessage?: string;
  isInvalidItem?: (item: T) => boolean;
  getInvalidItemMessage?: (item: T) => string | undefined;
  emptyMessage?: string;
}

export function TagMultiSelectInput<T extends BaseGenericType>({
  size,
  options,
  initialValues,
  onChange,
  isLoading,
  labelAttr = "label",
  renderDropdownItem = (item, _keyword) => <Box>{item[labelAttr]}</Box>,
  renderTagContent = (item) => <TagLabel>{item[labelAttr]}</TagLabel>,
  placeholder = "Select multiple",
  onAddTag,
  onRemoveTag,
  isDisabled = false,
  containerProps,
  selectedItemsContainerProps,
  inputContainerProps,
  label,
  labelProps,
  error,
  openDirection = "down",
  maxMenuHeight,
  validateSelectedItems = false,
  invalidSelectionErrorMessage = "One or more selected items are no longer available",
  leftIcon,
  isInvalidItem,
  getInvalidItemMessage,
  emptyMessage,
}: TagMultiSelectMenuProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: ref,
    handler: () => isOpen && closeMenu(),
  });

  const [inputValue, setInputValue] = React.useState<string>("");
  const [selectedItems, setSelectedItems] = React.useState<T[]>(initialValues);

  React.useEffect(() => {
    setSelectedItems(initialValues);
  }, [initialValues]);

  const getFilteredItemsForDropdown = useCallback(
    (selectedItems: T[], inputValue: string) => {
      const lowerCasedInputValue = inputValue?.toLowerCase() || "";
      return (options || []).filter(function filterItemsForDropdown(item) {
        return (
          !selectedItems?.find((selectedItem) => selectedItem.id === item.id) &&
          item[labelAttr]?.toLowerCase().includes(lowerCasedInputValue)
        );
      });
    },
    [options, labelAttr]
  );

  const itemsForDropdown: T[] = React.useMemo(() => {
    const notNullItems = selectedItems?.filter((i): i is T => !!i);
    return getFilteredItemsForDropdown(notNullItems, inputValue);
  }, [getFilteredItemsForDropdown, selectedItems, inputValue]);

  const { getSelectedItemProps, getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedItems(
            newSelectedItems?.filter((i): i is T => i !== undefined) ?? []
          );
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    getLabelProps,
  } = useCombobox({
    items: itemsForDropdown,
    itemToString(item) {
      return item ? item[labelAttr] : "";
    },
    defaultHighlightedIndex: 0,
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            inputValue: "",
            highlightedIndex: state.highlightedIndex,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    async onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (!newSelectedItem) break;
          const nextItems = [...selectedItems, newSelectedItem];
          if (onAddTag) {
            onAddTag(newSelectedItem);
          } else if (onChange) {
            onChange(nextItems);
          }
          setSelectedItems(nextItems);
          setInputValue("");
          break;

        case useCombobox.stateChangeTypes.InputChange:
          openMenu();
          setInputValue(newInputValue ?? "");
          break;

        case useCombobox.stateChangeTypes.InputBlur:
          closeMenu();
          break;

        default:
          break;
      }
    },
  });

  const openMenuUnlessDisabled = useCallback(() => {
    if (!isDisabled) {
      openMenu();
    }
  }, [isDisabled, openMenu]);

  const resolveIsInvalidItem = useCallback(
    (item: T) =>
      isInvalidItem
        ? isInvalidItem(item)
        : options.find((option) => option.id === item.id) === undefined,
    [isInvalidItem, options]
  );

  function* renderDropdownItems() {
    let group = "";
    for (const [index, item] of itemsForDropdown?.entries() ?? []) {
      if (item.group && item.group !== group) {
        group = item.group;
        yield (
          <Fragment key={`group-${group}`}>
            {index > 0 && <Divider />}
            <ListItem backgroundColor="white" listStyleType="none">
              <Flex align="center" minHeight={12} padding={2}>
                <Text fontSize="sm" fontWeight="bold">
                  {group} tags
                </Text>
              </Flex>
            </ListItem>
          </Fragment>
        );
      }
      yield (
        <ListItem
          key={item.id}
          backgroundColor={highlightedIndex === index ? "gray.200" : "white"}
          listStyleType="none"
          cursor="pointer"
          {...getItemProps({ item, index })}
        >
          <Flex align="center" minHeight={12} padding={2}>
            {renderDropdownItem(item, inputValue)}
          </Flex>
        </ListItem>
      );
    }
  }

  const hasInvalidSelection = useMemo(() => {
    if (!validateSelectedItems) {
      return false;
    }

    for (const selectedItem of selectedItems) {
      if (resolveIsInvalidItem(selectedItem)) {
        return true;
      }
    }

    return false;
  }, [selectedItems, validateSelectedItems, resolveIsInvalidItem]);

  error =
    hasInvalidSelection && error === undefined
      ? invalidSelectionErrorMessage
      : error;

  return (
    <FormControl
      as={Flex}
      flexDirection="column"
      gap="2"
      padding="1px"
      ref={ref}
      isInvalid={error !== undefined}
      {...containerProps}
    >
      {label !== undefined ? (
        <FormLabel margin="0" {...getLabelProps()} {...labelProps}>
          {label}
        </FormLabel>
      ) : null}
      {selectedItems.length > 0 ? (
        <Flex gap={1} flexWrap="wrap" {...selectedItemsContainerProps}>
          {selectedItems?.map(function renderSelectedItem(
            selectedItemForRender,
            index
          ) {
            return (
              <SelectedItemTag
                key={`selected-item-${index}`}
                selectedItem={selectedItemForRender}
                index={index}
                getSelectedItemProps={getSelectedItemProps}
                isInvalid={resolveIsInvalidItem(selectedItemForRender)}
                invalidItemMessage={getInvalidItemMessage?.(
                  selectedItemForRender
                )}
                size={size}
                isDisabled={isDisabled}
                onClose={(e) => {
                  e.stopPropagation();

                  const nextItems = selectedItems?.filter(
                    (i) => i !== selectedItemForRender
                  );

                  if (onRemoveTag) {
                    onRemoveTag(selectedItemForRender);
                  } else if (onChange) {
                    onChange(nextItems);
                  }
                  setSelectedItems(nextItems);
                }}
                renderTagContent={renderTagContent}
              />
            );
          })}
        </Flex>
      ) : null}
      <Box position="relative" {...inputContainerProps}>
        <InputGroup
          onClick={openMenuUnlessDisabled}
          onFocus={openMenuUnlessDisabled}
          display="flex"
          justifyContent="center"
        >
          {leftIcon === null ? null : (
            <InputLeftElement
              children={leftIcon ?? <SearchIcon color="gray.500" />}
            />
          )}

          <Input
            isDisabled={isDisabled}
            {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
            value={
              getInputProps(getDropdownProps({ preventKeyAction: isOpen }))
                .value || ""
            }
            placeholder={placeholder}
            size={size}
            aria-label={
              typeof label === "string" ? label : "Select multiple options"
            }
          />

          <InputRightElement children={<ChevronDownIcon color="gray.500" />} />
        </InputGroup>

        <UnorderedList
          {...getMenuProps()}
          aria-multiselectable="true"
          display={isOpen ? "block" : "none"}
          borderRadius="md"
          position="absolute"
          background="white"
          marginLeft={0}
          zIndex="200"
          boxShadow="lg"
          width="100%"
          overflow="scroll"
          maxHeight={maxMenuHeight || "280px"}
          marginTop={openDirection === "up" ? "calc(100% + 4px)" : undefined}
          bottom={openDirection === "up" ? "calc(100% + 4px)" : undefined}
          marginBottom={
            openDirection === "down" ? "calc(100% + 4px)" : undefined
          }
          top={openDirection === "down" ? "calc(100% + 4px)" : undefined}
        >
          {itemsForDropdown.length === 0 && emptyMessage !== undefined ? (
            <ListItem backgroundColor="white" listStyleType="none">
              <Flex align="center" minHeight={12} padding={2}>
                <Text color="gray.600">{emptyMessage}</Text>
              </Flex>
            </ListItem>
          ) : (
            [...renderDropdownItems()]
          )}
        </UnorderedList>
      </Box>
      {error !== undefined ? (
        <FormErrorMessage margin="0">{error}</FormErrorMessage>
      ) : null}
    </FormControl>
  );
}
